/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { observer } from 'mobx-react';
import { Box, CircularProgress, Divider, List, ListItem, ListItemText, Typography } from '@material-ui/core';
import PublicIcon from '@material-ui/icons/VolumeMute';
import { FormattedMessage } from 'react-intl';
import { Student } from '../../../../../types/Student';
import React, { useEffect } from 'react';
import ChatViewStyles from '../ChatViewStyles';
import { useViewModel } from '../../../../../hooks/useViewModel';
import { ChatListViewModel } from './ChatListViewModel';
import ChatUnreadNotificationView from '../../../../../components/Chat/ChatUnreadNotificationView';

const ChatListView = observer(() => {
	const classes = ChatViewStyles();
	const viewModel: ChatListViewModel = useViewModel(ChatListViewModel);
	const profileImageAlt = viewModel.formatMessage('chat.profile_image_alt');

	useEffect(() => {
		viewModel.loadActiveChat();
		return () => {
			viewModel.resetActiveChat();
		};
	}, []);

	return (
		<List className={classes.menuList}>
			{viewModel.areStudentsBeingFetched() ? (
				<Box className={classes.loadingContainer}>
					<CircularProgress className={classes.loading} />
				</Box>
			) : (
				<>
					<ListItem
						selected={viewModel.isActiveChat(viewModel.courseId)}
						button
						className={classes.listItem}
						onClick={() => viewModel.changeActiveChat(viewModel.courseId)}>
						<Box className={classes.publicIcon}>
							<PublicIcon style={{ fill: '#EAEAEF' }} />
						</Box>
						<ListItemText>
							<Typography className={classes.listItemText}>
								<FormattedMessage id={'chat.public'} />
							</Typography>
						</ListItemText>
						<ChatUnreadNotificationView unreadMessages={viewModel.getUnreadMessages(viewModel.courseId)} />
					</ListItem>
					<Divider variant='fullWidth' />
					{viewModel.getStudents().map((student: Student) => (
						<Box key={student.id}>
							<ListItem
								selected={viewModel.isActiveChat(viewModel.getRoomId(student.id))}
								button
								className={classes.listItem}
								onClick={() => viewModel.changeActiveChat(viewModel.getRoomId(student.id))}>
								<img
									src={viewModel.getStudentImgSrc(student)}
									alt={student.username + profileImageAlt}
									className={classes.image}
								/>
								<ListItemText>
									<Typography className={classes.listItemText}>{student.username}</Typography>
								</ListItemText>
								<ChatUnreadNotificationView
									unreadMessages={viewModel.getUnreadMessages(viewModel.getRoomId(student.id))}
								/>
							</ListItem>
							<Divider variant='fullWidth' />
						</Box>
					))}
				</>
			)}
		</List>
	);
});

export default ChatListView;
